import React from "react";
import { useState, useCallback } from "react";
import { Search, Plus, Eye, Loader2, AlertCircle, Check, RefreshCw, FileText, X, ImagePlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { ColumnVisibilityDropdown } from "./ColumnVisibilityDropdown";
import type { OptionalColumn } from "@/types/bulkAd";

interface AdSheetHeaderProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onAddRow: () => void;
  onReviewAds: () => void;
  rowCount: number;
  isSaving?: boolean;
  saveError?: string | null;
  sheetName?: string;
  isSyncing?: boolean;
  onRefreshData?: () => void;
  visibleOptionalColumns: OptionalColumn[];
  onToggleColumn: (column: OptionalColumn) => void;
  onApplyTemplate: () => void;
  selectedCount?: number;
  onClearSelection?: () => void;
  // Catalog selector props
  catalogId: string | null;
  onCatalogChange: (catalogId: string | null) => void;
  adAccountId?: string;
  userId?: string;
  onLoadCreatives?: () => void;
}

export function AdSheetHeader({
  searchQuery,
  onSearchChange,
  onAddRow,
  onReviewAds,
  rowCount,
  isSaving,
  saveError,
  sheetName,
  isSyncing,
  onRefreshData,
  visibleOptionalColumns,
  onToggleColumn,
  onApplyTemplate,
  selectedCount = 0,
  onClearSelection,
  catalogId,
  onCatalogChange,
  adAccountId,
  userId,
  onLoadCreatives,
}: AdSheetHeaderProps) {
  const [catalogInput, setCatalogInput] = useState(catalogId || "");

  // Keep local input in sync if catalogId changes externally
  const handleCatalogBlur = useCallback(() => {
    const trimmed = catalogInput.trim();
    if (trimmed && !/^\d+$/.test(trimmed)) return; // digits only
    onCatalogChange(trimmed || null);
  }, [catalogInput, onCatalogChange]);

  const handleCatalogClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCatalogInput("");
    onCatalogChange(null);
  };

  return (
    <header className="flex items-center justify-between gap-4 px-4 py-3 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex items-center gap-4">
        <h1 className="text-lg font-semibold text-foreground">{sheetName || "Ad Launcher V2"}</h1>
        <span className="text-sm text-muted-foreground">
          {rowCount} {rowCount === 1 ? "row" : "rows"}
        </span>
        {/* Selection indicator */}
        {selectedCount > 0 && (
          <div className="flex items-center gap-2 px-2 py-1 bg-primary/10 rounded-md">
            <span className="text-sm font-medium text-primary">{selectedCount} selected</span>
            <Button variant="ghost" size="sm" className="h-5 w-5 p-0 hover:bg-primary/20" onClick={onClearSelection}>
              <X className="h-3 w-3" />
            </Button>
          </div>
        )}
        {/* Save status indicator */}
        <div className="flex items-center gap-1.5 text-sm">
          {isSaving && (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
              <span className="text-muted-foreground">Saving...</span>
            </>
          )}
          {!isSaving && saveError && (
            <>
              <AlertCircle className="h-3.5 w-3.5 text-destructive" />
              <span className="text-destructive">Save failed</span>
            </>
          )}
          {!isSaving && !saveError && (
            <>
              <Check className="h-3.5 w-3.5 text-green-500" />
              <span className="text-muted-foreground">Saved</span>
            </>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative w-64">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search ads..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-8 h-8 text-sm bg-muted/50 border-border"
          />
        </div>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="sm" onClick={onRefreshData} className="h-8 gap-1.5" disabled={isSyncing}>
                <RefreshCw className={cn("h-4 w-4", isSyncing && "animate-spin")} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Syncs with Meta. Auto-refreshes every 3 mins.</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Catalog ID Input */}
        <div className="flex flex-col">
          <div className="relative">
            <Input
              type="text"
              placeholder="Catalog ID"
              value={catalogInput}
              onChange={(e) => setCatalogInput(e.target.value)}
              onBlur={handleCatalogBlur}
              onKeyDown={(e) => {
                if (e.key === "Enter") (e.target as HTMLInputElement).blur();
              }}
              className="h-8 w-[160px] text-xs pr-7"
            />
            {catalogInput && (
              <button
                type="button"
                className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                onClick={handleCatalogClear}
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
          <a
            href="https://business.facebook.com/commerce/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[10px] text-muted-foreground hover:underline mt-0.5"
          >
            Find in Commerce Manager → Settings
          </a>
        </div>

        <ColumnVisibilityDropdown visibleColumns={visibleOptionalColumns} onToggleColumn={onToggleColumn} />

        <Button variant="outline" size="sm" onClick={onApplyTemplate} className="h-8 gap-1.5">
          <FileText className="h-4 w-4" />
          Apply Template
        </Button>

        <Button variant="outline" size="sm" onClick={onLoadCreatives} className="h-8 gap-1.5">
          <ImagePlus className="h-4 w-4" />
          Load Creatives
        </Button>

        <Button variant="outline" size="sm" onClick={onAddRow} className="h-8 gap-1.5">
          <Plus className="h-4 w-4" />
          Add Row
        </Button>

        <Button size="sm" onClick={onReviewAds} className="h-8 gap-1.5">
          <Eye className="h-4 w-4" />
          Review Ads
        </Button>
      </div>
    </header>
  );
}
